/**
 * Read-only sibling notes for the exception drawer.
 *
 * Mirrors ``GET /admin/exception-notes/siblings``. Returns
 * notes on the same ``(fp_code, data_domain, object_id)`` from
 * OTHER exception threads (excluding the drawer's current thread).
 *
 * Same Evo/PSC gate as notes and tuple history - FP_USER is
 * short-circuited before any request goes out.
 */
import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { apiClient } from '../services/api'
import { canSeeExceptionNotes } from '../services/auth'

export interface SiblingNoteAuthor {
  id: number | null
  name: string | null
  email: string | null
  role: string | null
}

export interface SiblingNote {
  id: number
  exception_code: string
  exception_name: string
  data_domain: string
  object_id: string
  body: string
  created_at: string
  updated_at: string | null
  author: SiblingNoteAuthor
}

export interface SiblingNotesKey {
  fp_code: string
  data_domain: string
  object_id: string
  /** Exclude the drawer's current thread. Omit to return ALL notes on the
   *  object (used by the object-only drawer, which has no current code). */
  exclude_exception_code?: string
}

export interface UseSiblingNotesResult {
  notes: SiblingNote[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

export function useSiblingNotes(
  key: SiblingNotesKey | null,
): UseSiblingNotesResult {
  const { user } = useAuth()
  const gated = canSeeExceptionNotes(user)
  const [notes, setNotes] = useState<SiblingNote[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async (): Promise<void> => {
    if (!gated || !key) {
      setNotes([])
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await apiClient.get<{ notes: SiblingNote[] }>(
        '/admin/exception-notes/siblings',
        { params: key },
      )
      setNotes(res.data.notes)
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail || err?.message || 'Request failed'
      setError(String(detail))
    } finally {
      setLoading(false)
    }
  }, [gated, key])

  const keyHash = key
    ? `${key.fp_code}|${key.data_domain}|${key.object_id}|${key.exclude_exception_code ?? ''}`
    : null
  useEffect(() => {
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyHash])

  return { notes, loading, error, refresh }
}
